import type { FrontEventLogRequest } from './front-event-log.types';

const SENSITIVE_KEYS = [
  'signature',
  'firma',
  'token',
  'password',
  'pass',
  'authorization',
  'cookie',
  'sessionid',
  'x-app-key',
  'apikey',
  'key',
  'jwt',
  'bearer',
];

const MAX_BODY_BYTES = 128 * 1024;
const MAX_DETALLE = 16_000;
const MAX_DATOS_SERIALIZED = 16_000;

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEYS.some((s) => lower.includes(s));
}

function redactValue(value: unknown): unknown {
  if (typeof value === 'string') {
    if (value.startsWith('data:image/')) {
      return '[image]';
    }
    if (/^Bearer\s+/i.test(value)) {
      return '[redacted]';
    }
  }
  return value;
}

export function sanitizeDatosAdicionales(
  datos?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!datos) {
    return undefined;
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(datos)) {
    if (isSensitiveKey(key)) {
      out[key] = '[redacted]';
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      out[key] = sanitizeDatosAdicionales(value as Record<string, unknown>);
    } else {
      out[key] = redactValue(value);
    }
  }
  return truncateSerialized(out, MAX_DATOS_SERIALIZED) as Record<string, unknown>;
}

export function sanitizeDetalleTecnico(detalle?: string): string | undefined {
  if (!detalle) {
    return undefined;
  }
  let s = detalle;
  if (/data:image\//i.test(s)) {
    s = s.replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=]+/g, '[image]');
  }
  if (/Bearer\s+\S+/i.test(s)) {
    s = s.replace(/Bearer\s+\S+/gi, '[redacted]');
  }
  if (s.length > MAX_DETALLE) {
    return s.slice(0, MAX_DETALLE) + '…[truncated]';
  }
  return s;
}

function truncateSerialized(value: unknown, maxChars: number): unknown {
  const json = JSON.stringify(value);
  if (json.length <= maxChars) {
    return value;
  }
  return { _truncated: true, preview: json.slice(0, maxChars) + '…' };
}

export function ensureBodySizeLimit(body: FrontEventLogRequest): FrontEventLogRequest {
  const copy: FrontEventLogRequest = {
    ...body,
    detalleTecnico: sanitizeDetalleTecnico(body.detalleTecnico),
    datosAdicionales: sanitizeDatosAdicionales(body.datosAdicionales),
  };
  let json = JSON.stringify(copy);
  if (json.length <= MAX_BODY_BYTES) {
    return copy;
  }
  if (copy.detalleTecnico && copy.detalleTecnico.length > 500) {
    copy.detalleTecnico = copy.detalleTecnico.slice(0, 500) + '…[truncated]';
    json = JSON.stringify(copy);
  }
  if (json.length > MAX_BODY_BYTES && copy.datosAdicionales) {
    copy.datosAdicionales = truncateSerialized(
      copy.datosAdicionales,
      2000
    ) as Record<string, unknown>;
  }
  return copy;
}
